import React, { useContext, useMemo, useState } from 'react'
import { TabMenu, TabMenuButton, Text, View } from 'oio-react'
import { useParams } from 'react-router-dom'
import { initiativeUrl } from 'config/constants/urls'
import ContainerBlock from 'src/apps/CustomApp/blocks/Container'
import LayoutContainer from 'src/apps/CustomApp/components/LayoutContainer'
import Title from 'src/apps/CustomApp/components/Title'
import Titlebar from 'src/apps/CustomApp/components/Titlebar'
import InitiativeCreateControl from 'src/apps/Initiative/components/CreateControl'
import InitiativeList from 'src/apps/Initiative/components/List'
import { useOrganization } from 'src/graphql/hooks'
import ProtoThemeContext from 'src/libraries/contexts/ProtoTheme'
import DiscussionListItemRow from 'src/apps/Discussions/components/ListItemRow'

const FILTER_BY_RECENT = 'recent'
const FILTER_BY_POPULARITY = 'popularity'
const FILTER_BY_FOLLOWING = 'following'

const DiscussionsHome = () => {
   const { initiativeTypeSlug } = useParams()
   const { protoTheme: theme } = useContext(ProtoThemeContext)
   const { organization } = useOrganization()
   const [initiativeListFilterMode, setInitiativeListFilterMode] = useState(FILTER_BY_RECENT)

   const discussionInitiativeTypes = organization.initiativeTypes
      .filter(t => t.enabled && t.class === 'discussion')

   // If there is an initiativeTypeSlug, show only discussions of that type
   // Otherwise, we assume it is a full Groups app and show all discussions of
   // any initiative type
   const initiativeTypes = useMemo(() => {
      if (initiativeTypeSlug) {
         const initiativeType = organization.initiativeTypes
            .find(type => type.slug === initiativeTypeSlug)

         if (initiativeType) {
            return [initiativeType]
         }
      }

      return organization.initiativeTypes
         .filter(type => type.enabled && type.class === 'discussion')
   }, [organization.initiativeTypes, initiativeTypeSlug])

   const initiativeTypeIds = initiativeTypes.map(type => type.id)
   const appTitle = initiativeTypeSlug && initiativeTypes[0]
      ? initiativeTypes[0].namePlural
      : 'Discussions'

   const initiativeListFilter = {
      archived: 'exclude',
      drafts: 'include',
      sortBy: 'dateLastUpdated',
      typeIds: initiativeTypeIds,
      limit: 25
   }

   if (initiativeListFilterMode === FILTER_BY_POPULARITY) {
      initiativeListFilter.sortBy = 'popularity'
   } else if (initiativeListFilterMode === FILTER_BY_FOLLOWING) {
      initiativeListFilter.userRelationshipFilter = {
         userId: 'me',
         hasElementType: 'subscribed'
      }
   }

   return (
      <LayoutContainer>
         <ContainerBlock>
            <Titlebar>
               <Title color={theme.tmpContentTitleColor}>{appTitle}</Title>
               <InitiativeCreateControl initiativeTypes={discussionInitiativeTypes} />
            </Titlebar>
            <TabMenu
               buttonPaddingHorizontal="0px"
               buttonSpacing="20px"
               buttonSize="lg"
               buttonTextSize="1.5"
               highlightColor="#222">
               <TabMenuButton
                  isActive={initiativeListFilterMode === FILTER_BY_RECENT}
                  onClick={() => setInitiativeListFilterMode(FILTER_BY_RECENT)}
                  name="Most Recent"
               />
               <TabMenuButton
                  isActive={initiativeListFilterMode === FILTER_BY_POPULARITY}
                  onClick={() => setInitiativeListFilterMode(FILTER_BY_POPULARITY)}
                  name="Most Popular"
               />
               <TabMenuButton
                  isActive={initiativeListFilterMode === FILTER_BY_FOLLOWING}
                  onClick={() => setInitiativeListFilterMode(FILTER_BY_FOLLOWING)}
                  name="Following"
               />
            </TabMenu>
            <InitiativeList
               emptyContent={(
                  <View width="100%" padding="30px 0px">
                     <Text size="2" color="#aaa">No discussion threads to show</Text>
                  </View>
               )}
               filter={initiativeListFilter}>
               {initiativeList => initiativeList.items.map((initiative, index) => (
                  <DiscussionListItemRow
                     key={initiative.id}
                     addedBy={initiative.addedBy.fullName}
                     archived={initiative.archived}
                     authorBadges={initiative.addedBy.badges}
                     backgroundColor={index % 2 ? 'transparent' : '#fafafa'}
                     borderStyle="none"
                     coverUrl={initiative.coverMedia?.file.thumbnailUrlW480}
                     dateLastUpdated={initiative.dateLastUpdated}
                     discussionFormat={initiative.discussionFormat}
                     discussionIsClosed={initiative.discussionStatus?.closed}
                     hasUnreadEntries={initiative.thread.hasUnreadEntries}
                     linkTo={initiativeUrl(initiative)}
                     name={initiative.name}
                     numThreadEntries={initiative.thread.numEntries}
                     numViews={initiative.numViews}
                     pinned={initiative.pinned}
                     privacy={initiative.privacy}
                     slug={initiative.slug}
                     typeSlug={initiative.type.slug}
                  />
               ))}
            </InitiativeList>
         </ContainerBlock>
      </LayoutContainer>
   )
}

export default DiscussionsHome
